import type { InventoryOrderCarTireQuoteRecord } from '@navio/nsdk';
import { useAppForm } from 'noddi-ui-web';
import { z } from 'zod';

const storageUnitOptionSchema = z.object({ value: z.number(), label: z.string() });

type StorageUnitOption = z.input<typeof storageUnitOptionSchema>;

const MISSING_STORAGE_UNIT = 'Choose a storage unit';

/** One unit for the whole delivery, or one per order: only the visible choice is required. */
const getValidationSchema = (inventoryOrderIds: number[]) =>
  z
    .object({
      isPerOrder: z.boolean(),
      sharedStorageUnit: storageUnitOptionSchema.optional(),
      storageUnits: z.record(z.string(), storageUnitOptionSchema.optional())
    })
    .superRefine(({ isPerOrder, sharedStorageUnit, storageUnits }, ctx) => {
      if (!isPerOrder) {
        if (!sharedStorageUnit) {
          ctx.addIssue({ code: 'custom', path: ['sharedStorageUnit'], message: MISSING_STORAGE_UNIT });
        }
        return;
      }
      for (const id of inventoryOrderIds) {
        if (!storageUnits[id]) {
          ctx.addIssue({ code: 'custom', path: ['storageUnits', String(id)], message: MISSING_STORAGE_UNIT });
        }
      }
    });

type FormValues = z.input<ReturnType<typeof getValidationSchema>>;

interface PlacedInventoryOrder {
  order: InventoryOrderCarTireQuoteRecord;
  storageUnit: StorageUnitOption;
}

type Params = {
  orders: InventoryOrderCarTireQuoteRecord[];
  /** False when the orders go to different facilities: a unit belongs to one, so each order gets its own. */
  canShareStorageUnit: boolean;
  onSubmit: (placed: PlacedInventoryOrder[]) => Promise<void>;
};

export const useReceiveStorageUnitsForm = ({ orders, canShareStorageUnit, onSubmit }: Params) => {
  const defaultValues: FormValues = {
    isPerOrder: !canShareStorageUnit,
    sharedStorageUnit: undefined,
    storageUnits: {}
  };
  return useAppForm({
    defaultValues,
    validators: { onSubmit: getValidationSchema(orders.map((order) => order.id)) },
    onSubmit: ({ value }) =>
      onSubmit(
        orders.flatMap((order) => {
          const storageUnit = value.isPerOrder ? value.storageUnits[order.id] : value.sharedStorageUnit;
          return storageUnit ? [{ order, storageUnit }] : [];
        })
      )
  });
};

export type ReceiveStorageUnitsForm = ReturnType<typeof useReceiveStorageUnitsForm>;
